import { API_CONFIG, API_ENDPOINTS, authFetch, getHeadersWithOrg } from './client';
import {
  ComponentCatalogItem,
  ComponentSchemaGroup,
  SampleElement,
} from '../../features/template-designer/model/types';

/**
 * The component catalog endpoint lives on the publishing/kg service (the same
 * base URL `publishFromMap` uses), NOT on the platform service — it is the owner
 * of the built-in content components and their style schema. Like every kg
 * endpoint it is served under the `/flameback/api/v1` prefix.
 */
const COMPONENTS_PATH = API_ENDPOINTS.COMPONENTS;

/** Wire shape of one catalog item: snake_case keys as the backend emits them. */
interface RawComponentCatalogItem {
  id: string;
  base_tag: string;
  class_binding: string | null;
  style: Record<string, unknown>;
  schema?: RawComponentSchemaGroup[];
  html?: string;
  css?: string;
  css_is_template?: boolean;
  js?: string;
  sample?: RawComponentSample | null;
}

/**
 * Wire shape of a component sample block (PARITY §7): `children_html` is snake_case;
 * `el` is a `SampleElement` whose `_innerHtml` is ALREADY the camelCase wire key
 * (FastAPI emits the alias), so the tree passes through with no key transform.
 */
interface RawComponentSample {
  el: SampleElement;
  children_html: string;
}

/** Wire shape of one schema group: snake_case to match the rest of the response. */
interface RawComponentSchemaGroup {
  group: string;
  variant_key?: string;
  variant_options?: string[];
  fields: ComponentSchemaGroup['fields'];
}

/** Map a wire group to the camelCase model shape (field keys stay verbatim). */
function mapGroup(group: RawComponentSchemaGroup): ComponentSchemaGroup {
  if (!group.variant_key && group.fields?.length) {
    console.warn('[mapGroup] variant_key missing from wire response — variant-based styling will not work for this group:', group);
  }
  return {
    group: group.group,
    variantKey: group.variant_key,
    variantOptions: group.variant_options,
    fields: group.fields,
  };
}

/** Map a wire catalog item to the camelCase model shape. */
function mapItem(item: RawComponentCatalogItem): ComponentCatalogItem {
  if (!item.id) console.warn('[mapItem] component missing id — it will be unfindable in the registry:', item);
  if (item.css_is_template && !item.css) console.warn(`[mapItem] component "${item.id}" has css_is_template=true but no css — CSS render will produce no styles`);
  return {
    id: item.id,
    baseTag: item.base_tag,
    classBinding: item.class_binding,
    style: item.style,
    schema: item.schema?.map(mapGroup),
    html: item.html,
    css: item.css,
    cssIsTemplate: item.css_is_template,
    js: item.js,
    sample: item.sample
      ? { el: item.sample.el, childrenHtml: item.sample.children_html }
      : null,
  };
}

/**
 * Fetch the stylable content-component catalog from the publishing/kg service.
 *
 * The catalog is static for a session (it describes built-in components, not
 * per-site data), so callers cache the result. A non-OK status throws; the
 * caller degrades to an empty catalog.
 */
export async function getComponents(): Promise<ComponentCatalogItem[]> {
  const response = await authFetch(`${API_CONFIG.BASE_URL}${COMPONENTS_PATH}`, {
    headers: getHeadersWithOrg(),
  });
  if (!response.ok) {
    throw new Error(`Failed to load components: ${response.status}`);
  }
  const raw = (await response.json()) as RawComponentCatalogItem[];
  return raw.map(mapItem);
}
